import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import Space from "antd/es/space";
import { defineMessages, useIntl } from "react-intl";

import { Button, Modal } from "@mapmycustomers/ui";

import AccessChangeOption from "@app/enum/AccessChangeOption";
import { isAccessChangePromptVisible } from "@app/store/accessChange";
import { chooseAccessChangeOption } from "@app/store/accessChange/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "./AccessChangeModal.module.scss";

const messages = defineMessages({
  description: {
    id: "component.accessChangeModal.description",
    defaultMessage: "An administrator changed your access after this page was opened.",
    description: "Access change modal: why the modal is shown",
  },
  keepPage: {
    id: "component.accessChangeModal.options.keepPage",
    defaultMessage: "Keep this page to copy my work",
    description:
      "Access change modal: button that leaves the page as it is so the user can copy input",
  },
  keepPageDescription: {
    id: "component.accessChangeModal.options.keepPage.description",
    defaultMessage: "<b>Keep this page to copy my work</b>: saving may fail until you reload.",
    description: "Access change modal: what the keep this page button does",
  },
  reload: {
    id: "component.accessChangeModal.options.reload",
    defaultMessage: "Reload page",
    description: "Access change modal: reload button",
  },
  reloadDescription: {
    id: "component.accessChangeModal.options.reload.description",
    defaultMessage: "<b>Reload page</b>: start fresh. Anything you haven't saved will be lost.",
    description: "Access change modal: what the reload button does",
  },
  title: {
    id: "component.accessChangeModal.title",
    defaultMessage: "Your access has changed",
    description: "Access change modal: title",
  },
  updatePage: {
    id: "component.accessChangeModal.options.updatePage",
    defaultMessage: "Update this page",
    description: "Access change modal: update this page button",
  },
  updatePageDescription: {
    id: "component.accessChangeModal.options.updatePage.description",
    defaultMessage: "<b>Update this page</b>: no reload, but the page may reset or break.",
    description: "Access change modal: what the update this page button does",
  },
});

const boldText = { b: (chunks: ReactNode[]) => <b>{chunks}</b> };

const AccessChangeModal = ({
  onChoose,
  open,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const handleReload = useCallback(() => onChoose(AccessChangeOption.RELOAD), [onChoose]);
  const handleUpdatePage = useCallback(() => onChoose(AccessChangeOption.UPDATE_PAGE), [onChoose]);
  const handleKeepPage = useCallback(() => onChoose(AccessChangeOption.KEEP_PAGE), [onChoose]);

  return (
    <Modal
      closable={false}
      footer={
        <Space className={styles.footer} direction="vertical">
          <Button block onClick={handleReload} type="primary">
            {intl.formatMessage(messages.reload)}
          </Button>
          <Button block onClick={handleUpdatePage}>
            {intl.formatMessage(messages.updatePage)}
          </Button>
          <Button block onClick={handleKeepPage}>
            {intl.formatMessage(messages.keepPage)}
          </Button>
        </Space>
      }
      keyboard={false}
      maskClosable={false}
      open={open}
      size="small"
      title={intl.formatMessage(messages.title)}
      type="warning"
    >
      <p>{intl.formatMessage(messages.description)}</p>
      <ul className={styles.options}>
        <li>{intl.formatMessage(messages.reloadDescription, boldText)}</li>
        <li>{intl.formatMessage(messages.updatePageDescription, boldText)}</li>
        <li>{intl.formatMessage(messages.keepPageDescription, boldText)}</li>
      </ul>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  open: isAccessChangePromptVisible(state),
});

const mapDispatchToProps = {
  onChoose: chooseAccessChangeOption,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccessChangeModal);
